import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from 'react-router-dom';
import { EventsState } from "../../../../../../store/types";
import { Session, SessionTypesFeatures } from "../../../../../../types/working-model";
import Chip from "../../../../../general-ui/chip/chip";
import Icon, { COLORS, ICONS } from "../../../../../general-ui/icon";
import { PATHNAMES } from "../../../../admin";
import { ISessionFilters } from "../panel/event/submenus/sessions-panel";

interface ISessionListProps {
	sessions: Session[];
	filters: ISessionFilters;
	handleResetFilters: () => void;
}

const SessionList: React.FC<ISessionListProps> = ({
	sessions,
	filters,
	handleResetFilters,
}) => {

	const { eventUuid } = useSelector((state: EventsState) => ({
		eventUuid: state.CreateEventReducer.workingEvent?.uuid,
	}));

	const languages = (session: Session): JSX.Element | null => {
		const { languages } = session;
		const sortedLanguages = [session.default_language, ...languages.filter((lang: string) => lang !== session.default_language).sort()];

		if (!sortedLanguages.length) return null;
		const firstThree = sortedLanguages.slice(0, 3);
		const remainder = sortedLanguages.slice(3);
		return (
			<div className="session-panel-list-language-container">
				{firstThree.map((lang: string) => (
					<div key={lang} className="session-panel-list-language">{lang}</div>
				))}
				{remainder.length ? (
					<div className="session-panel-list-language">+{remainder.length}</div>
				) : null}
			</div>
		);
	};

	const filteredSessionsMemoized = useMemo(() => () => {
		if (!sessions.length) return [];
		const trimmedSearch = filters.searchText.trim();
		if (!filters.category && !filters.language && !filters.date && !trimmedSearch.length) {
			return sessions;
		}
		return sessions.filter((session: Session) => {
			if (filters.category && !session.tracks?.includes(filters.category)) return false;
			if (filters?.language?.code && !session.languages.find(lang => lang === filters?.language?.code)) return false;
			if (filters.date && session.timestamp && new Date(session.timestamp).toDateString() !== filters.date) return false;
			if (trimmedSearch.length && !session.title.base.toLowerCase().includes(trimmedSearch.toLowerCase())) return false;
			return true;
		});
	}, [filters, sessions]);

	const filteredSessions = filteredSessionsMemoized();

	const filterEnabled = filters?.category || filters?.date || filters?.language || filters?.searchText?.trim()?.length;

	return (
		<div>
			{filterEnabled ? (
				<div className="session-panel-filter-result-info">
					<div>{filteredSessions.length} session{filteredSessions.length === 1 ? '' : 's'} found</div>
					<button
						className="clear no-style"
						onClick={handleResetFilters}
					>
						<Icon name={ICONS.CLOSE} color={COLORS.CYAN} size={12} /> <span>Clear filters</span>
					</button>
				</div>
			) : null}
			<ul className="session-panel-list-by-type">
				{filteredSessions.map(session => (
					<li className="session-panel-card" key={session.session}>
						<div className="session-panel-card-info">
							{session?.timestamp ? (
								<div className="session-panel-card-time">
									{new Date(session.timestamp).toLocaleString([], {
										year: 'numeric',
										month: 'short',
										day: 'numeric',
										hour: '2-digit',
										minute: '2-digit',
									})}
								</div>
							) : null}
							<h6 className="session-panel-card-title">{session.title.base}</h6>
							<div className="session-panel-card-type-lang">
								{session?.timestamp ? (
									<div className="session-name-chip-container">
										<Chip>
											<div className="session-name-chip">
												{SessionTypesFeatures?.[session.session_type]?.icon_name ? (
													<Icon
														name={SessionTypesFeatures[session.session_type].icon_name as string}
														color={COLORS.WHITE}
														size={14}
													/>
												) : null}
												<span>{SessionTypesFeatures?.[session.session_type]?.display_name}</span>
											</div>
										</Chip>
									</div>
								) : null}
								{languages(session)}
							</div>
						</div>
						<div className="session-panel-card-link">
							<Link role="button" className="no-style clear" to={PATHNAMES.Session.SessionLink(eventUuid!, session.uuid, session.default_language)}>
								<Icon name={ICONS.ARROW_RIGHT} color={COLORS.WHITE} size={16} />
							</Link>
						</div>
					</li>
				))}
			</ul>
		</div>
	);
};

export default SessionList;
